import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { format, addDays } from 'date-fns';
import { ArrowLeft, AlertTriangle, Calendar, Check, ChevronsUpDown } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/app/components/ui/select';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/app/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/app/components/ui/popover';
import { Alert, AlertDescription, AlertTitle } from '@/app/components/ui/alert';
import { toast } from '@/app/components/ui/simple-toaster';
import { cn } from '@/app/components/ui/utils';
import { useAsset, useAssets, useCheckOutAsset } from '@/hooks/use-assets';
import { useLocations, useOrganizationUsers } from '@/hooks/use-deployments';
import { formatDateOnly } from '@/lib/date-utils';
import type { Asset, CheckOutPayload } from '@/lib/types';

const checkOutSchema = z.object({
  asset: z.number({ error: 'Please select an asset' }).min(1, 'Please select an asset'),
  assigned_to_user: z.number().optional(),
  location: z.number().optional(),
  due_date: z.string().optional(),
  notes: z.string().optional(),
});

type CheckOutFormData = z.infer<typeof checkOutSchema>;

export function CheckOutPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const preselectedAssetIdParam = searchParams.get('asset');
  const preselectedAssetId = preselectedAssetIdParam ? parseInt(preselectedAssetIdParam, 10) : 0;
  const [assetSearch, setAssetSearch] = useState('');
  const [assetOpen, setAssetOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);

  const { data: assetsData, isLoading: assetsLoading } = useAssets({
    page_size: 50,
    q: assetSearch,
  });
  const { data: locations } = useLocations();
  const { data: users } = useOrganizationUsers();
  const checkOutMutation = useCheckOutAsset();

  // Filter to show only assets that can be checked out (active or available status)
  const availableAssets = (assetsData?.results || []).filter(
    (asset) => asset.status === 'active' || asset.status === 'available'
  );

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CheckOutFormData>({
    resolver: zodResolver(checkOutSchema),
    defaultValues: {
      due_date: format(addDays(new Date(), 7), 'yyyy-MM-dd'),
    },
  });

  const { data: preselectedAsset } = useAsset(preselectedAssetId);

  useEffect(() => {
    if (preselectedAsset && !selectedAsset) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSelectedAsset(preselectedAsset);
      setValue('asset', preselectedAsset.id);
    }
  }, [preselectedAsset, selectedAsset, setValue]);


  const getAssetWarnings = (asset: Asset | null): string[] => {
    if (!asset) return [];
    const warnings: string[] = [];

    if (asset.is_overdue) {
      warnings.push('This asset has an overdue inspection. Consider scheduling an inspection before deployment.');
    }

    if (asset.next_inspection_due) {
      const dueDate = new Date(asset.next_inspection_due);
      const today = new Date();
      const daysUntilDue = Math.ceil((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      if (daysUntilDue <= 7 && daysUntilDue > 0) {
        warnings.push(`Inspection due in ${daysUntilDue} day(s). Consider scheduling before deployment.`);
      }
    }

    if (asset.status !== 'available' && asset.status !== 'active') {
      warnings.push(`Asset status is "${asset.status}". Only active or available assets can be checked out.`);
    }

    return warnings;
  };

  const assetWarnings = getAssetWarnings(selectedAsset);

  const onSubmit = async (data: CheckOutFormData) => {
    try {
      const payload: CheckOutPayload = {
        assigned_to_user: data.assigned_to_user,
        location: data.location,
        due_date: data.due_date,
        notes: data.notes,
      };

      await checkOutMutation.mutateAsync({ id: data.asset, data: payload });
      toast.success('Asset checked out successfully');
      navigate('/deployments');
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to check out asset';
      if (typeof error === 'object' && error !== null && 'response' in error) {
        const axiosError = error as { response?: { data?: { detail?: string; error?: string } } };
        const detail = axiosError.response?.data?.detail || axiosError.response?.data?.error;
        if (detail) {
          toast.error(detail);
          return;
        }
      }
      toast.error(errorMessage);
    }
  };

  const handleAssetSelect = (asset: Asset) => {
    setSelectedAsset(asset);
    setValue('asset', asset.id);
    setAssetOpen(false);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/deployments')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Check Out Asset</h1>
          <p className="text-gray-600 mt-1">Deploy equipment to users or locations</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Asset Selection</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Select Asset *</Label>
              <Popover open={assetOpen} onOpenChange={setAssetOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    aria-expanded={assetOpen}
                    className={cn(
                      'w-full justify-between',
                      !selectedAsset && 'text-muted-foreground'
                    )}
                  >
                    {selectedAsset ? (
                      <span>
                        {selectedAsset.display_name} ({selectedAsset.serial_number})
                      </span>
                    ) : (
                      'Search for an available asset...'
                    )}
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-full p-0" align="start">
                  <Command>
                    <CommandInput
                      placeholder="Search by name or serial number..."
                      value={assetSearch}
                      onValueChange={setAssetSearch}
                    />
                    <CommandList>
                      <CommandEmpty>
                        {assetsLoading ? 'Loading...' : 'No available assets found.'}
                      </CommandEmpty>
                      <CommandGroup>
                        {availableAssets.map((asset) => (
                          <CommandItem
                            key={asset.id}
                            value={`${asset.display_name} ${asset.serial_number}`}
                            onSelect={() => handleAssetSelect(asset)}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedAsset?.id === asset.id ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            <div className="flex flex-col">
                              <span className="font-medium">{asset.display_name}</span>
                              <span className="text-sm text-gray-500">
                                {asset.serial_number}
                                {asset.manufacturer && ` - ${asset.manufacturer}`}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              {errors.asset && (
                <p className="text-sm text-red-500">{errors.asset.message}</p>
              )}
            </div>

            {selectedAsset && (
              <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                <h4 className="font-medium text-gray-900">Selected Asset Details</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-gray-500">Name:</span>{' '}
                    <span className="font-medium">{selectedAsset.display_name}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Serial:</span>{' '}
                    <span className="font-medium">{selectedAsset.serial_number}</span>
                  </div>
                  {selectedAsset.manufacturer && (
                    <div>
                      <span className="text-gray-500">Manufacturer:</span>{' '}
                      <span className="font-medium">{selectedAsset.manufacturer}</span>
                    </div>
                  )}
                  {selectedAsset.length_m && (
                    <div>
                      <span className="text-gray-500">Length:</span>{' '}
                      <span className="font-medium">{selectedAsset.length_m}m</span>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Status:</span>{' '}
                    <span className="font-medium capitalize">{selectedAsset.status}</span>
                  </div>
                  {selectedAsset.next_inspection_due && (
                    <div>
                      <span className="text-gray-500">Next Inspection:</span>{' '}
                      <span className={cn('font-medium', selectedAsset.is_overdue && 'text-red-600')}>
                        {formatDateOnly(selectedAsset.next_inspection_due, '—', {
                          month: 'short',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {assetWarnings.length > 0 && (
              <Alert variant="destructive">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Warnings</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc list-inside space-y-1">
                    {assetWarnings.map((warning, index) => (
                      <li key={index}>{warning}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Deployment Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="assigned_to_user">Assign To User</Label>
                <Select
                  onValueChange={(value) =>
                    setValue('assigned_to_user', value ? parseInt(value, 10) : undefined)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a user (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {users?.map((user) => (
                      <SelectItem key={user.id} value={user.id.toString()}>
                        {user.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="location">Deployment Location</Label>
                <Select
                  onValueChange={(value) =>
                    setValue('location', value ? parseInt(value, 10) : undefined)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select a location (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations?.map((location) => (
                      <SelectItem key={location.id} value={location.id.toString()}>
                        {location.name}
                        <span className="text-gray-500 ml-2 text-xs capitalize">
                          ({location.type})
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="due_date">Expected Return Date</Label>
              <div className="relative max-w-xs">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  id="due_date"
                  type="date"
                  className="pl-10"
                  min={format(new Date(), 'yyyy-MM-dd')}
                  {...register('due_date')}
                />
              </div>
              <p className="text-sm text-gray-500">
                The deployment will be marked as overdue after this date.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                placeholder="Add any special instructions or notes about this deployment..."
                rows={3}
                {...register('notes')}
              />
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end gap-4">
          <Button type="button" variant="outline" onClick={() => navigate('/deployments')}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Checking Out...' : 'Check Out Asset'}
          </Button>
        </div>
      </form>
    </div>
  );
}
